import type { Pt } from './types';

/*
 * Vector content of a PDF (lines, filled shapes, texts with their colours),
 * read from pdf.js operator lists. Written against the few pdf.js calls it
 * needs, so it runs in the browser and in Node without depending on pdf.js
 * itself. Everything comes out in millimetres with y pointing down.
 */

export interface PdfPath {
  /** "#rrggbb" or undefined when not stroked / filled */
  stroke?: string;
  fill?: string;
  /** line width in mm */
  width: number;
  /** dashed (structure boxes and the like: drawn, but never part of a device) */
  dash?: boolean;
  /** from a DXF: the block the shape was inserted with */
  blk?: string;
  /** subpaths (curves flattened) */
  polys: { pts: Pt[]; closed: boolean }[];
}

export interface PdfText {
  s: string;
  /** start of the baseline, mm */
  x: number;
  y: number;
  /** font size in mm */
  size: number;
  /** direction in degrees, clockwise on the page (0 = left to right, -90 = reading upwards) */
  angle: number;
  /** advance length of the text in mm */
  w: number;
  font: string;
  bold: boolean;
  colour: string;
}

export interface PdfPageData {
  /** page size in mm */
  w: number;
  h: number;
  /** bookmark title pointing at this page, if any */
  title: string;
  paths: PdfPath[];
  texts: PdfText[];
}

/* The parts of the pdf.js API used here. */
interface PdfJsPage {
  getViewport(o: { scale: number }): { transform: number[]; width: number; height: number };
  getOperatorList(): Promise<{ fnArray: number[]; argsArray: any[] }>;
  commonObjs: { get(name: string, callback?: (data: any) => void): any; has?(name: string): boolean };
  cleanup?(): void;
}
export interface PdfJsDocument {
  numPages: number;
  getPage(n: number): Promise<PdfJsPage>;
  getOutline(): Promise<any[] | null>;
  getDestination(id: string): Promise<any[] | null>;
  getPageIndex(ref: any): Promise<number>;
}

/* pdf.js operator codes (OPS) and path drawing codes (DrawOPS); stable across pdf.js versions. */
const OP = {
  setLineWidth: 2, save: 10, restore: 11, transform: 12, stroke: 20, closeStroke: 21, fill: 22, eoFill: 23, fillStroke: 24, eoFillStroke: 25,
  closeFillStroke: 26, closeEOFillStroke: 27, endPath: 28, beginText: 31, setCharSpacing: 33, setWordSpacing: 34, setHScale: 35, setLeading: 36,
  setFont: 37, setTextRise: 39, moveText: 40, setLeadingMoveText: 41, setTextMatrix: 42, nextLine: 43, showText: 44, showSpacedText: 45,
  nextLineShowText: 46, nextLineSetSpacingShowText: 47, setStrokeRGBColor: 58, setFillRGBColor: 59, paintFormXObjectBegin: 74, paintFormXObjectEnd: 75,
  constructPath: 91,
} as const;
const DRAW = { moveTo: 0, lineTo: 1, curveTo: 2, quadraticCurveTo: 3, closePath: 4 } as const;
const MM = 25.4 / 72;

type M = number[];
const mul = (m: M, n: M): M => [
  m[0] * n[0] + m[2] * n[1], m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3], m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4], m[1] * n[4] + m[3] * n[5] + m[5],
];
const ap = (m: M, x: number, y: number): Pt => [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
const r3 = (v: number) => Math.round(v * 1000) / 1000;

/** Bookmark titles by page index (the shallowest bookmark wins). */
async function pageTitles(doc: PdfJsDocument): Promise<Map<number, string>> {
  const out = new Map<number, string>();
  let outline: any[] | null = null;
  try {
    outline = await doc.getOutline();
  } catch {
    return out;
  }
  const walk = async (items: any[], depth: number) => {
    for (const it of items ?? []) {
      if (depth <= 2) {
        try {
          const dest = typeof it.dest === 'string' ? await doc.getDestination(it.dest) : it.dest;
          if (Array.isArray(dest) && dest[0] != null) {
            const pi = typeof dest[0] === 'number' ? dest[0] : await doc.getPageIndex(dest[0]);
            if (!out.has(pi)) out.set(pi, String(it.title ?? '').trim());
          }
        } catch {
          /* broken bookmark */
        }
        await walk(it.items, depth + 1);
      }
    }
  };
  await walk(outline ?? [], 0);
  return out;
}

/** Reads one page's lines, filled shapes and texts. */
export async function extractPdfPage(page: PdfJsPage, title = ''): Promise<PdfPageData> {
  const vp = page.getViewport({ scale: 1 });
  const base: M = vp.transform.map((v, i) => (i < 4 ? v * MM : v * MM));
  const ops = await page.getOperatorList();
  const paths: PdfPath[] = [], texts: PdfText[] = [];
  interface St { ctm: M; stroke: string; fill: string; lw: number; font: any; fontName: string; size: number; tm: M; lm: M; cs: number; ws: number; hs: number; lead: number; rise: number }
  let st: St = { ctm: base, stroke: '#000000', fill: '#000000', lw: 1, font: null, fontName: '', size: 0, tm: [1, 0, 0, 1, 0, 0], lm: [1, 0, 0, 1, 0, 0], cs: 0, ws: 0, hs: 1, lead: 0, rise: 0 };
  const stack: St[] = [];
  const scaleOf = (m: M) => Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2]));

  const addPath = (paint: number, data: ArrayLike<number> | undefined) => {
    if (!data) return;
    const stroke = [OP.stroke, OP.closeStroke, OP.fillStroke, OP.eoFillStroke, OP.closeFillStroke, OP.closeEOFillStroke].includes(paint as never);
    const fill = [OP.fill, OP.eoFill, OP.fillStroke, OP.eoFillStroke, OP.closeFillStroke, OP.closeEOFillStroke].includes(paint as never);
    if (!stroke && !fill) return;
    const polys: PdfPath['polys'] = [];
    let cur: Pt[] = [], closed = false, last: Pt = [0, 0];
    const flush = () => {
      if (cur.length > 1) polys.push({ pts: cur.map((p) => [r3(p[0]), r3(p[1])] as Pt), closed });
      cur = [];
      closed = false;
    };
    for (let i = 0; i < data.length;) {
      const op = data[i++];
      if (op === DRAW.moveTo) {
        flush();
        last = [data[i++], data[i++]];
        cur.push(ap(st.ctm, ...last));
      } else if (op === DRAW.lineTo) {
        last = [data[i++], data[i++]];
        cur.push(ap(st.ctm, ...last));
      } else if (op === DRAW.curveTo || op === DRAW.quadraticCurveTo) {
        const n = op === DRAW.curveTo ? 3 : 2;
        const c: Pt[] = [last];
        for (let k = 0; k < n; k++) c.push([data[i++], data[i++]]);
        // flatten: 8 steps are plenty for symbol circles and arcs
        for (let t = 1; t <= 8; t++) {
          const u = t / 8, v = 1 - u;
          const p: Pt = n === 3
            ? [v * v * v * c[0][0] + 3 * v * v * u * c[1][0] + 3 * v * u * u * c[2][0] + u * u * u * c[3][0], v * v * v * c[0][1] + 3 * v * v * u * c[1][1] + 3 * v * u * u * c[2][1] + u * u * u * c[3][1]]
            : [v * v * c[0][0] + 2 * v * u * c[1][0] + u * u * c[2][0], v * v * c[0][1] + 2 * v * u * c[1][1] + u * u * c[2][1]];
          cur.push(ap(st.ctm, ...p));
        }
        last = c[n];
      } else if (op === DRAW.closePath) {
        closed = true;
        if (cur.length) cur.push(cur[0]);
      } else break;
    }
    flush();
    if (polys.length) paths.push({ ...(stroke ? { stroke: st.stroke } : {}), ...(fill ? { fill: st.fill } : {}), width: r3(st.lw * scaleOf(st.ctm)), polys });
  };

  const show = (glyphs: any[]) => {
    if (!st.font || !st.size) return;
    const fm = st.font.fontMatrix ?? [0.001, 0, 0, 0.001, 0, 0];
    let s = '', adv = 0;
    for (const g of glyphs) {
      if (typeof g === 'number') {
        adv -= (g * st.size) / 1000;
        if (g < -200) s += ' ';
        continue;
      }
      if (!g) continue;
      s += g.unicode ?? '';
      adv += ((g.width ?? 0) * st.size * fm[0] + st.cs + (g.isSpace ? st.ws : 0)) * st.hs;
    }
    const trm = mul(st.ctm, mul(st.tm, [1, 0, 0, 1, 0, st.rise]));
    const o = ap(trm, 0, 0), e = ap(trm, adv, 0), up = ap(trm, 0, st.size);
    const size = Math.hypot(up[0] - o[0], up[1] - o[1]);
    if (s.trim())
      texts.push({
        s: s.replace(/\s+/g, ' ').trim(), x: r3(o[0]), y: r3(o[1]), size: r3(size),
        angle: r3((Math.atan2(e[1] - o[1], e[0] - o[0]) * 180) / Math.PI), w: r3(Math.hypot(e[0] - o[0], e[1] - o[1])),
        font: String(st.font.name ?? st.fontName), bold: !!st.font.bold || /bold|black|heavy/i.test(String(st.font.name ?? '')), colour: st.fill,
      });
    st.tm = mul(st.tm, [1, 0, 0, 1, adv, 0]);
  };
  const moveLine = (x: number, y: number) => {
    st.lm = mul(st.lm, [1, 0, 0, 1, x, y]);
    st.tm = st.lm.slice();
  };

  const { fnArray: fns, argsArray: args } = ops;
  // fonts arrive from the worker on their own; wait for the ones this page uses (name, bold)
  const fonts = new Map<string, any>();
  await Promise.all(
    [...new Set(fns.flatMap((fn, i) => (fn === OP.setFont ? [args[i][0] as string] : [])))].map(
      (name) =>
        new Promise<void>((done) => {
          const timer = setTimeout(done, 3000);
          page.commonObjs.get(name, (data: any) => {
            fonts.set(name, data);
            clearTimeout(timer);
            done();
          });
        }),
    ),
  );
  for (let i = 0; i < fns.length; i++) {
    const fn = fns[i], a = args[i];
    switch (fn) {
      case OP.save: stack.push({ ...st }); break;
      case OP.restore: st = stack.pop() ?? st; break;
      case OP.transform: st.ctm = mul(st.ctm, a); break;
      case OP.paintFormXObjectBegin:
        stack.push({ ...st });
        if (Array.isArray(a?.[0]) || ArrayBuffer.isView(a?.[0])) st.ctm = mul(st.ctm, Array.from(a[0] as ArrayLike<number>));
        break;
      case OP.paintFormXObjectEnd: st = stack.pop() ?? st; break;
      case OP.setLineWidth: st.lw = a[0]; break;
      case OP.setStrokeRGBColor: st.stroke = typeof a[0] === 'string' ? a[0] : st.stroke; break;
      case OP.setFillRGBColor: st.fill = typeof a[0] === 'string' ? a[0] : st.fill; break;
      case OP.constructPath: addPath(a[0], a[1]?.[0]); break;
      case OP.beginText: st.tm = [1, 0, 0, 1, 0, 0]; st.lm = [1, 0, 0, 1, 0, 0]; break;
      case OP.setFont: {
        st.fontName = a[0];
        st.font = fonts.get(a[0]) ?? { name: a[0] };
        st.size = a[1];
        break;
      }
      case OP.setCharSpacing: st.cs = a[0]; break;
      case OP.setWordSpacing: st.ws = a[0]; break;
      case OP.setHScale: st.hs = a[0] / 100; break;
      case OP.setLeading: st.lead = a[0]; break;
      case OP.setTextRise: st.rise = a[0]; break;
      case OP.setTextMatrix: { const m = Array.from(a[0]?.length === 6 ? a[0] : a) as number[]; st.tm = m.slice(); st.lm = m.slice(); break; }
      case OP.moveText: moveLine(a[0], a[1]); break;
      case OP.setLeadingMoveText: st.lead = -a[1]; moveLine(a[0], a[1]); break;
      case OP.nextLine: moveLine(0, -st.lead); break;
      case OP.showText: case OP.showSpacedText: show(a[0]); break;
      case OP.nextLineShowText: moveLine(0, -st.lead); show(a[0]); break;
      case OP.nextLineSetSpacingShowText: st.ws = a[0]; st.cs = a[1]; moveLine(0, -st.lead); show(a[2]); break;
    }
  }
  return { w: r3(vp.width * MM), h: r3(vp.height * MM), title, paths, texts };
}

/** Reads every page of a PDF; `progress` is told the page being read. */
export async function extractPdf(doc: PdfJsDocument, progress?: (page: number, of: number) => void): Promise<PdfPageData[]> {
  const titles = await pageTitles(doc);
  const out: PdfPageData[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    progress?.(n, doc.numPages);
    const page = await doc.getPage(n);
    out.push(await extractPdfPage(page, titles.get(n - 1) ?? ''));
    page.cleanup?.();
  }
  return out;
}
